import { useState } from 'react';

import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  Container,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SearchIcon from '@mui/icons-material/Search';

import axios from 'axios';
import useSWR from 'swr';

import Error from './_error';

import BottomNav from '../components/BottomNav';
import BusinessCard from '../components/BusinessCard';
import GoogleMaps from '../components/GoogleMaps';
import HeroCondensed from '../components/HeroCondensed';
import Link, { NextLinkComposed } from '../components/Link';
import Paginator from '../components/Paginator';

import TopNav from '../components/TopNav';
import WaivingBuddy from '../components/WaivingBuddy';

import cars from '../constants/cars';

import carInsuranceCG from '../public/partners/v2/500x200-compare-guru-insurance.png';
import costEstimateIcon from '../public/img/new-estimate-dark-mode.png';
import mobileCG from '../public/partners/v2/325x80-compare-guru-insurance.png';


const compareKeys = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

const fetcher = (url, query) => {
  const { car, near, page } = query;
  return axios({
    baseURL: process.env.DOMAIN,
    url,
    params: {
      car,
      near,
      page,
    },
  }).then(res => res.data);
};

function BusinessListing(props) {
  const router = useRouter();

  const initialCar = router.query.car ? router.query.car : '';
  const initialNear = router.query.near ? router.query.near : '';

  const [car, setCar] = useState(initialCar);
  const [near, setNear] = useState(initialNear);

  // show loading if not original request
  const initialQuery = props.query;
  const fallbackData = compareKeys(initialQuery, router.query)
    ? props.data
    : undefined;

  // maybe limit this to car & near only instead of entire query
  const { data, error } = useSWR(
    ['/content/business/', router.query],
    fetcher,
    { fallbackData }
  );

  if (props.statusCode) {
    return <Error statusCode={props.statusCode} />;
  }

  const handleSearch = () => {
    event.preventDefault();

    router.push(
      {
        pathname: router.pathname,
        query: { car, near },
      },
      {
        pathname: router.pathname,
        query: { car, near },
      },
      { shallow: true }
    );
  };

  const handleNearChange = value => {
    setNear(value);
  };

  const currentPage = parseInt(router.query.page) || 1;
  const pageUp = parseInt(currentPage) ? currentPage + 1 : 2;
  const pageDown = parseInt(currentPage) > 1 ? currentPage - 1 : null;

  const nextUrl = {
    pathname: router.pathname,
    query: {
      ...router.query,
      page: pageUp,
    },
  };

  const previousUrl = {
    pathname: router.pathname,
    query: {
      ...router.query,
      page: pageDown,
    },
  };

  const pageTitle = `The Best Rated ${initialCar || ''} Mechanics near ${
    initialNear || 'You'
  }`;
  const pageDescription = `Get your car serviced and repaired by the best rated ${
    initialCar || ''
  } mechanics and workshops near ${
    initialNear || 'you. View profiles with user reviews, images and more.'
  }`;

  const headerTitle = `The Best ${initialCar || ''} Mechanics near You`;
  const resultSummary = `Showing ${data && data.count} ${
    initialCar || ''
  } mechanics and workshops${initialNear ? ' near ' + initialNear : ''}`;

  return <>
    <Head>
      <title>{pageTitle} - MechanicBuddy</title>
      <meta name="description" content={pageDescription} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content={`https://www.mechanicbuddy.co.za${router.asPath}`}
      />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta
        property="og:image"
        content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
      />

      {/* Twitter */}
      <meta property="twitter:card" content="summary" />
      <meta
        property="twitter:url"
        content={`https://www.mechanicbuddy.co.za${router.asPath}`}
      />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta
        property="twitter:image"
        content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
      />

      <link
        rel="canonical"
        href={`https://www.mechanicbuddy.co.za${router.asPath}`}
      />
      {data && data.count === 0 && <meta name="robots" content="noindex" />}
    </Head>

    <TopNav color="transparent" />

    <HeroCondensed
      title={headerTitle}
      description={<span>View workshop profiles with user reviews, images, contact <br/>information and more</span>}
      overlay={true}
    />

    <Container>
      <Box bgcolor="white" p={3} my={4}>
        <form onSubmit={() => handleSearch()}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            <Box
              flex={1}
              mr={{ xs: 0, md: 3 }}
              mb={{ xs: 2, md: 0 }}
              width="100%"
            >
              <Autocomplete
                options={cars}
                value={car}
                autoHighlight={true}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Any Car"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <IconButton edge="start" size="large">
                              <DirectionsCarIcon />
                            </IconButton>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, value) => {
                  setCar(value);
                }}
              />
            </Box>

            <Box
              flex={1}
              mr={{ xs: 0, md: 3 }}
              mb={{ xs: 2, md: 0 }}
              width="100%"
            >
              <GoogleMaps
                onInputChange={handleNearChange}
                initialValue={initialNear}
                types={['(regions)']}
                autoFocus={false}
              />
            </Box>

            <Box width={{ xs: '100%', md: 'unset' }} textAlign="center">
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Box>
          </Box>
        </form>
      </Box>

      <Grid
        container
        columnSpacing={3} 
        direction="row"
      >
        <Grid item xs={12} md={8}>
          {error && <div>Something went wrong ...</div>}
          {!data && (
            <Box bgcolor="white" pt={2}>
              <Box
                fontSize="subtitle1.fontSize"
                component="h4"
                pl={2}
                fontWeight={400}
              >
                Loading ...
              </Box>
              <Box
                sx={{
                  p: 2,
                  '& .MuiSkeleton-root': {
                    mb: 2,
                  },
                }}
              >
                <Skeleton variant="rectangular" width="100%" height={160} />
                <Skeleton variant="rectangular" width="100%" height={160} />
                <Skeleton variant="rectangular" width="100%" height={160} />
              </Box>
            </Box>
          )}

          {data && data.count > 0 && (
            <Box
              sx={{
                bgcolor: {
                  xs: 'unset',
                  md: 'white',
                },
                pt: {
                  xs: 0,
                  md: 2,
                }
              }}
            >
              <Box
                fontSize="subtitle1.fontSize"
                component="h3"
                pl={2}
                fontWeight={400}
              >
                {resultSummary}
              </Box>
              {data.results.map(business => (
                <BusinessCard key={business.url_name} business={business} />
              ))}
            </Box>
          )}

          {data && data.count === 0 && (
            <Box
              sx={{
                '& .MuiTypography-root': {
                  mt: 2,
                },
                mb: 4,
                textAlign: 'center',
                p: 2,
                bgcolor: 'white',
              }}
            >
              <Image
                src="/icons/404.svg"
                width={200}
                height={200}
                alt="End of road"
              />
              <Typography variant="h5">
                No {initialCar} mechanics or workshops found
              </Typography>
              <Typography>
                Sorry, curently we don&apos;t have any {initialCar} mechanics or
                shops listed near {initialNear}.<br />
                <Link
                  color="secondary"
                  href={{
                    pathname: '/best-mechanics',
                    query: { near: initialNear },
                  }}
                  onClick={() => {
                    setCar(null);
                  }}
                >
                  Click here
                </Link>{' '}
                to view other mechanics and workshop near {initialNear}.
              </Typography>
              {/* Maybe add option to request mechanics in this area */}
            </Box>
          )}

          <Box
            width="100%"
            display={{xs: "flex", md: "none"}}
            flexDirection="row"
            justifyContent="center"
          >
            <a
              href="https://compareguru.co.za/insurance-quotes/car-insurance/personal?channel=mechanic-buddy"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <Image
                src={mobileCG}
                alt="CompareGuru car insurance"
                quality={100}
              />
            </a>
          </Box>

          {data && data.count > 0 && (
            <Paginator
              pageDown={pageDown}
              previousUrl={previousUrl}
              currentPage={currentPage}
              totalPages={data.total_pages}
              nextUrl={nextUrl}
              pageUp={pageUp}
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Hidden mdDown implementation="css">
            <a
              href="https://compareguru.co.za/insurance-quotes/car-insurance/personal?channel=mechanic-buddy"
              target="_blank"
              rel="noreferrer"
            >
              <Image
                src={carInsuranceCG}
                alt="CompareGuru car insurance"
                quality={100}
              />
            </a>

            <Box
              p={2}
              color="white"
              bgcolor="primary.main"
              mt={6}
              textAlign="center"
            >
              <Box mb={2} fontSize="h4.fontSize" fontWeight="500">
                Quick Quotes
              </Box>
              <Image
                src={costEstimateIcon}
                alt="Get cost estimates"
              />
              <Box my={2} fontSize="body1.fontSize">
                Way easier than phoning <br />mechanics yourself ...
              </Box>
              <Button
                size="large"
                variant="contained"
                color="secondary"
                to={{
                  pathname: `/request-quotes/`,
                }}
                component={NextLinkComposed}
              >Request Quotes</Button>
            </Box>
          </Hidden>
        </Grid>
      </Grid>
    </Container>

    <WaivingBuddy />

    <BottomNav />
  </>;
}

export async function getServerSideProps({ query, res }) {
  const { data, statusCode } = await fetcher('/content/business/', query)
    .then(data => {
      return {
        data,
        statusCode: null,
      };
    })
    .catch(error => {
      return {
        data: null,
        statusCode: error.response.status,
      };
    });

  if (statusCode === 404) {
    return {
      notFound: true
    }
  }

  return {
    props: {
      data,
      statusCode,
      query,
    },
  };
}

export default BusinessListing;
